import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { IndianRupee, TrendingUp, Trash2, Pencil, ChevronRight, ChevronDown } from 'lucide-react';

// Shared column layout so the 3 stacked tables align vertically.
// First column: fixed 240px. Actions column: fixed 90px. Months share the rest equally.
const monthColStyle = { minWidth: '110px' };
const FIRST_COL_CLASS = 'w-60';
const ACTIONS_COL_CLASS = 'w-24';

const MonthHeaderCells = ({ months }) => months.map(m => (
  <TableHead key={m.key} className="text-right" style={monthColStyle}>{m.name}</TableHead>
));

export const IncomeTable = ({ months, incomeByMonth, formatCurrency, onCellClick }) => (
  <div className="mb-6">
    <h4 className="font-medium mb-2 text-green-600 flex items-center gap-2">
      <IndianRupee className="h-4 w-4" /> Income (from Fee Structure)
    </h4>
    <Table className="w-full table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead className={FIRST_COL_CLASS}>Category</TableHead>
          <MonthHeaderCells months={months} />
          <TableHead className={ACTIONS_COL_CLASS}></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow>
          <TableCell className="font-medium">Expected Income</TableCell>
          {months.map(m => (
            <TableCell
              key={m.key}
              className="text-right text-green-600 font-semibold cursor-pointer hover:bg-green-50 dark:hover:bg-green-950/30 transition-colors rounded"
              onClick={() => onCellClick('income', m.key, m.name)}
              data-testid={`income-cell-${m.key}`}
            >
              <span className="underline decoration-dotted underline-offset-4">
                {formatCurrency(incomeByMonth[m.key])}
              </span>
            </TableCell>
          ))}
          <TableCell></TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
);

const ExpenseSubRow = ({ months, sub, formatCurrency, onEditExpense, onDeleteExpense }) => {
  return (
    <TableRow className="bg-muted/20" data-testid={`expense-subrow-${sub.subHead || 'blank'}`}>
      <TableCell className="pl-10 text-sm text-muted-foreground">{sub.subHead || '(no sub-head)'}</TableCell>
      {months.map(m => {
        const entry = sub.byMonth[m.key];
        const hasAmount = entry?.amount > 0;
        const hasExpense = Boolean(entry?.expenseId);
        return (
          <TableCell
            key={m.key}
            className="text-right text-red-500 text-sm group relative"
          >
            {hasAmount ? (
              <span className="relative inline-block">
                <span
                  className={hasExpense ? 'cursor-pointer hover:underline' : ''}
                  onClick={() => { if (hasExpense) onEditExpense(entry.expenseId); }}
                  title={hasExpense ? 'Click to edit this month' : ''}
                >
                  {formatCurrency(entry.amount)}
                </span>
                {hasExpense && (
                  <span
                    className="absolute right-full top-1/2 -translate-y-1/2 mr-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-background/95 border rounded shadow-sm px-0.5 z-10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="p-0.5 rounded hover:bg-muted"
                      onClick={() => onEditExpense(entry.expenseId)}
                      title="Edit this month's expense"
                      data-testid={`edit-cell-${sub.subHead || 'blank'}-${m.key}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      className="p-0.5 rounded hover:bg-muted"
                      onClick={() => onDeleteExpense(entry.expenseId)}
                      title="Delete this month's expense"
                      data-testid={`delete-cell-${sub.subHead || 'blank'}-${m.key}`}
                    >
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </button>
                  </span>
                )}
              </span>
            ) : '-'}
          </TableCell>
        );
      })}
      <TableCell></TableCell>
    </TableRow>
  );
};

const ExpenseGroupRows = ({ group, months, formatCurrency, onEditExpense, onDeleteExpense, isExpanded, onToggleExpand }) => {
  const hasSubGroups = group.subGroups && group.subGroups.length > 0;
  const canExpand = hasSubGroups && !group.isReimbursement;
  return (
    <>
      <TableRow data-testid={`expense-head-row-${group.head}`}>
        <TableCell className="font-medium">
          <button
            type="button"
            className="inline-flex items-center gap-1 hover:opacity-80"
            onClick={() => onToggleExpand(group.head)}
            disabled={!canExpand}
            data-testid={`expand-head-${group.head}`}
          >
            {canExpand ? (
              isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
            ) : <span className="w-4 inline-block" />}
            {group.head}
          </button>
        </TableCell>
        {months.map(m => (
          <TableCell key={m.key} className="text-right text-red-600 font-medium">
            {group.byMonth[m.key]?.amount > 0 ? formatCurrency(group.byMonth[m.key].amount) : '-'}
          </TableCell>
        ))}
        <TableCell></TableCell>
      </TableRow>
      {isExpanded && hasSubGroups && group.subGroups.map((sub) => (
        <ExpenseSubRow
          key={`${group.head}-${sub.subHead || 'blank'}`}
          months={months}
          sub={sub}
          formatCurrency={formatCurrency}
          onEditExpense={onEditExpense}
          onDeleteExpense={onDeleteExpense}
        />
      ))}
    </>
  );
};

export const ExpensesTable = ({ months, expenseGroups, expensesByMonth, formatCurrency, onCellClick, onEditExpense, onDeleteExpense, expandedHeads = {}, onToggleExpand = () => {} }) => (
  <div className="mb-6">
    <Table className="w-full table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead className={FIRST_COL_CLASS}>Expense Head</TableHead>
          <MonthHeaderCells months={months} />
          <TableHead className={ACTIONS_COL_CLASS}></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {expenseGroups.length === 0 ? (
          <TableRow>
            <TableCell colSpan={months.length + 2} className="text-center text-muted-foreground py-4">
              No expenses added yet
            </TableCell>
          </TableRow>
        ) : (
          expenseGroups.map((group) => (
            <ExpenseGroupRows
              key={group.head}
              group={group}
              months={months}
              formatCurrency={formatCurrency}
              onEditExpense={onEditExpense}
              onDeleteExpense={onDeleteExpense}
              isExpanded={Boolean(expandedHeads[group.head])}
              onToggleExpand={onToggleExpand}
            />
          ))
        )}
        <TableRow className="bg-red-50 dark:bg-red-950/20 font-semibold">
          <TableCell>Total Expenses</TableCell>
          {months.map(m => (
            <TableCell
              key={m.key}
              className="text-right text-red-600 cursor-pointer hover:bg-red-100 dark:hover:bg-red-950/40 transition-colors rounded"
              onClick={() => onCellClick('expense', m.key, m.name)}
              data-testid={`expense-total-cell-${m.key}`}
            >
              <span className="underline decoration-dotted underline-offset-4">
                {formatCurrency(expensesByMonth[m.key])}
              </span>
            </TableCell>
          ))}
          <TableCell></TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
);

export const NetCashFlowTable = ({ months, netByMonth, formatCurrency, onCellClick }) => (
  <div>
    <h4 className="font-medium mb-2 flex items-center gap-2">
      <TrendingUp className="h-4 w-4" /> Net Cash Flow
    </h4>
    <Table className="w-full table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead className={FIRST_COL_CLASS}>Category</TableHead>
          <MonthHeaderCells months={months} />
          <TableHead className={ACTIONS_COL_CLASS}></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow className="bg-muted/50 font-semibold">
          <TableCell>Net Cash Flow</TableCell>
          {netByMonth.map((item) => (
            <TableCell
              key={item.month}
              className={`text-right cursor-pointer hover:bg-muted transition-colors rounded ${item.net >= 0 ? 'text-green-600' : 'text-red-600'}`}
              onClick={() => onCellClick('net', item.month, months.find(m => m.key === item.month)?.name || item.month)}
              data-testid={`net-cell-${item.month}`}
            >
              <span className="underline decoration-dotted underline-offset-4">
                {formatCurrency(Math.abs(item.net))}
                {item.net < 0 && <span className="text-xs ml-1">(deficit)</span>}
              </span>
            </TableCell>
          ))}
          <TableCell></TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
);
